import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { generatePath } from 'react-router'

import { useListRequest, useRequest } from '../hooks/request'
import { userListVerbose, userDelete } from '../requests/user'
import { UserContext } from '../data/UserContext'
import { hasRole } from '../utils/hasRole'
import { ROLES, ROLES_ARRAY } from '../constants/roles'
import { PATHS } from '../constants/paths'
import { VARIANT_SECONDARY } from '../constants/variants'

import { PageTitle } from './common/PageTitle'
import { PageContent } from './layout/layout'
import { List } from './common/List'
import { Trad } from './common/Trad'
import { DeleteButton } from './common/ConfirmButton'
import { H3 } from './common/Typography'
import { Margin } from './common/Margin'
import { ListSearch } from './common/ListSearch'
import { GoToItemCreate, DummyButton } from './common/Button'
import { SearchCreateWrapper } from './common/SearchCreateWrapper'

const HiddenButton = styled(DummyButton)`
  padding: 3px;
  visibility: hidden;
`

const titles = [
  { title: 'fullName' },
  { title: 'email' },
  { title: 'role' },
  { size: 0, content: <HiddenButton /> },
]
const getRowLink = (item) => generatePath(PATHS.USER_EDIT, { id: item.id })

const displayItem = (item, props) => [
  `${item.firstName} ${item.lastName}`,
  item.email,
  { content: <Trad id={item.role} /> },
  {
    content: (
      <DeleteButton
        loading={props.deletingId === item.id}
        titleTrad="confirmDeleteUser"
        onConfirm={() => props.onConfirmDelete({ id: item.id })}
      />
    ),
  },
]

const roleMap = {
  [ROLES.INTERNSHIP_DIRECTOR]: [ROLES.INTERNSHIP_DIRECTOR, ROLES.BASIC_USER],
  [ROLES.ADMIN]: ROLES_ARRAY,
  [ROLES.SUPER_ADMIN]: ROLES_ARRAY,
}

export const UserList = () => {
  const { user } = useContext(UserContext)
  const roleFilter = roleMap[user.role]
  const userListRequest = useListRequest({
    startFilter: {
      role: roleFilter,
    },
    start: true,
    request: userListVerbose,
  })

  const userDeleteRequest = useRequest({
    request: userDelete,
    handler: (p) => p.then(userListRequest.fetch),
  })

  const conditionnalProps = useMemo(() => (hasRole(user, [ROLES.ADMIN, ROLES.SUPER_ADMIN]) ? {
    getRowLink,
  } : {}))

  return (
    <>
      <PageTitle tradId="userList" />
      <PageContent>
        <SearchCreateWrapper>
          <GoToItemCreate tradId="userCreate" to={PATHS.USER_CREATE} />
          <Margin v={10} />
          <div>
            <H3 tradId="searchTitle" bold variant={VARIANT_SECONDARY} />
            <Margin v={15} />
            <ListSearch onSearch={userListRequest.setSearch} placeholder="nameOrEmail" />
          </div>
        </SearchCreateWrapper>
        <List
          onConfirmDelete={userDeleteRequest.fetch}
          listRequest={userListRequest}
          columnTitles={titles}
          displayItem={displayItem}
          {...conditionnalProps}
        />
      </PageContent>
    </>
  )
}
